import { useScroll, useMotionValueEvent, MotionValue } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'

import { Button, Icon } from 'components/ui'
import { Divider } from 'components/ui/Divider'
import { cn } from 'lib/utils/tailwind'

type NewCarouselProps = {
  className?: string
  children: React.ReactNode
  noProgressBar?: boolean
}

export const NewCarousel: React.FC<NewCarouselProps> = ({
  className,
  children,
  noProgressBar = false,
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const scrollBar = useRef<HTMLDivElement>(null)
  const { scrollXProgress } = useScroll({ container: ref })

  useEffect(() => {
    const scrollableEl = ref.current
    const scrollBarEl = scrollBar.current
    if (!scrollableEl || !scrollBarEl) return

    const resizeObserver = new ResizeObserver(() => {
      const ratio = scrollableEl.offsetWidth / scrollableEl.scrollWidth
      const scrollbarWidth = ratio * 100

      scrollBarEl.style.setProperty('--scrollbar-width:', `${scrollbarWidth}%`)
      scrollBarEl.style.width = `${scrollbarWidth}%`
    })

    resizeObserver.observe(scrollableEl)

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  useMotionValueEvent(scrollXProgress, 'change', (latest) => {
    const scrollableEl = ref.current
    const scrollBarEl = scrollBar.current

    if (scrollBarEl && scrollableEl && scrollBarEl) {
      const ratio = scrollableEl.offsetWidth / scrollableEl.scrollWidth
      scrollBarEl.style.setProperty('--ratio', `${ratio}`)
      scrollBarEl.style.setProperty('--progress', `${latest}`)
    }
  })

  const scrollOnClick = (direction: string) => {
    const scrollableEl = ref.current
    if (!scrollableEl) return

    const scrollAmount = scrollableEl.offsetWidth * 0.8 // Scroll 80% of visible width
    scrollableEl.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    })
  }

  return (
    <div className="flex flex-col gap-10">
      <div
        ref={ref}
        className={cn(
          'overflow-x-auto overflow-y-hidden [&::-webkit-scrollbar]:hidden [scrollbar-width:none] max-h-[288px] md:max-h-[600px]',
          className
        )}
      >
        {children}
      </div>
      {!noProgressBar && (
        <div className="flex items-center gap-3 w-full">
          <CarouselButton
            onClick={() => scrollOnClick('left')}
            direction="left"
            scrollXProgress={scrollXProgress}
          />
          <div className="relative w-full flex items-center">
            <Divider />
            <div
              className="absolute h-px bg-foreground translate-x-[calc((100%*(1/var(--ratio))-100%)*var(--progress))]"
              ref={scrollBar}
            />
          </div>
          <CarouselButton
            onClick={() => scrollOnClick('right')}
            direction="right"
            scrollXProgress={scrollXProgress}
          />
        </div>
      )}
    </div>
  )
}

type CarouselButtonProps = {
  onClick: () => void
  direction: string
  scrollXProgress: MotionValue<number>
}

const CarouselButton = ({
  onClick,
  direction,
  scrollXProgress,
}: CarouselButtonProps) => {
  const [isDisabled, setIsDisabled] = useState(direction === 'left')

  useMotionValueEvent(scrollXProgress, 'change', (latest) => {
    setIsDisabled(
      (latest === 0 && direction === 'left') ||
        (latest === 1 && direction === 'right')
    )
  })

  return (
    <Button
      variant="outline"
      size="sm"
      square
      className={cn(
        'rounded-full border-none size-10 pt-4 transition-all duration-300',
        isDisabled
          ? 'pointer-events-none opacity-40 bg-transparent'
          : 'bg-foreground/5'
      )}
      onClick={onClick}
    >
      <Icon
        icon={direction === 'left' ? 'arrow-left' : 'arrow-right'}
        size={16}
      />
    </Button>
  )
}
